/** 작업 하나의 **서버 응답 전체**(`DetailJobView`)를 읽는 훅.
 *
 * ## 왜 셸의 컨텍스트로 안 받는가
 *
 * `JobShell` 이 `Outlet context` 로 주는 것은 `ShellJob` 이고 **그것은 머리줄이 쓰는 축소본**
 * 이다(상품명·칩·장수·단계). `W-31`·`W-30`·`W-40` 은 그 축소본으로 그릴 수 없다 — 장별 상태,
 * 블록 켜짐, 배경 hex, 사진 파일명이 전부 빠져 있다.
 *
 * 컨텍스트 모양을 넓히는 대신 **같은 질의를 한 번 더 구독한다.** 키가 `["job", jobId]` 로
 * 셸과 같아서 캐시가 한 벌이고, `staleTime` 이 5초라 셸이 막 받아 온 직후에 붙는 이 구독은
 * **다시 요청하지 않는다**(`apps/web/src/main.tsx`). 컨텍스트를 넓히면 그 값을 안 쓰는 화면
 * 넷까지 타입이 바뀌므로, 필요한 화면만 무는 이쪽이 싸다.
 *
 * ## 예시 작업 갈래는 없다
 *
 * 셸(`JobShell`)은 모든 작업 id 를 서버에 묻는다. 없는 id 는 서버가 404 를 주고 셸이
 * 「찾을 수 없습니다」를 그린다. 예시 작업을 가리던 `isSample` 과 `findSampleJob` 은
 * 2026-10-05 에 걷었다. 이 훅도 셸과 같은 조건(`enabled: Boolean(jobId)`)으로 묻는다.
 *
 * `view === null` 은 「아직 안 왔다」와 「못 받았다」를 구분하지 못한다. 화면은 `isLoading` 과
 * `isError` 를 함께 본다.
 */

import { useQuery } from "@tanstack/react-query";
import type { DetailJobView } from "@makit/shared";

import { getJob } from "./api";

export interface JobViewState {
  /** 서버 작업의 응답 전체. 아직 안 왔거나 못 받았으면 `null`. */
  view: DetailJobView | null;
  /** 아직 오지 않았다. */
  isPending: boolean;
  /** 못 받았다. */
  isError: boolean;
  refetch: () => void;
}

export function useJobView(jobId: string | undefined): JobViewState {
  const query = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => getJob(jobId as string),
    // ⛔ 셸(`JobShell`)의 조건과 **같아야 한다.** 키가 같아 캐시를 함께 쓴다.
    enabled: Boolean(jobId),
  });

  return {
    view: query.data ?? null,
    isPending: query.isPending,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
